import type { AxiosInstance, AxiosResponse } from "axios";
import axios from "axios";
import { toast } from "sonner";

import { ROUTES } from "@/constants/routes";
import { globalNavigate } from "@/lib/navigation";
import { setupTokenRefreshInterceptor } from "@/lib/tokenRefreshManager";
import { authService } from "@/services/authService";
import { useUserStore } from "@/store/userStore";

function applyCommonInterceptors(client: AxiosInstance) {
  client.interceptors.request.use(
    (config) => {
      const token = authService.getToken();
      if (token && config.headers) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  setupTokenRefreshInterceptor(
    client,
    () => authService.getToken(),
    () => authService.refreshToken(),
    () => {
      useUserStore.getState().clearUser();
      globalNavigate(ROUTES.login);
    },
    {
      skipUrls: ["/v1/auth/login", "/v1/auth/register", "/v1/auth/refresh-token", "/v1/auth/logout"],
    }
  );

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
      if (error.response?.data) {
        const errorData = error.response.data;
        const detailMessage =
          errorData.detail ||
          errorData.message ||
          errorData.error ||
          (typeof errorData === "string" ? errorData : null);

        if (detailMessage) {
          error.message = detailMessage;
        }
      }

      if (error.response?.status === 402) {
        toast.error("Недостаточно топлива. Пополните баланс для продолжения торговли.");
      }

      return Promise.reject(error);
    }
  );
}

// Main API client (server service)
export const apiClient = axios.create({
  baseURL: "/api",
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

applyCommonInterceptors(apiClient);

// Helper to create API clients for external services
function createServiceClient(baseURL: string, timeout = 15000) {
  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      "Content-Type": "application/json",
    },
  });

  applyCommonInterceptors(client);
  return client;
}

// Exchanges API client (separate service)
export const exchangesApiClient = createServiceClient("/exchanges");

// Signals API client (separate service)
export const signalsApiClient = createServiceClient("/signals");

export default apiClient;
